const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

export class WhatsAppCallClient {
  constructor({ callId, userToken, userId, onState, onError }) {
    this.callId = callId;
    this.userToken = userToken;
    this.userId = userId;
    this.onState = onState || (() => {});
    this.onError = onError || (() => {});
    this.ws = null;
    this.pc = null;
    this.localStream = null;
    this.remoteStream = null;
    this.audioEl = null;
  }

  bridgeUrl() {
    const params = new URLSearchParams({
      call_id: this.callId,
      token: this.userToken,
      user_id: String(this.userId || ''),
    });
    const configured = window.chatwootConfig?.whatsappCallBridgeUrl;
    if (configured) {
      const sep = configured.includes('?') ? '&' : '?';
      return `${configured}${sep}${params.toString()}`;
    }
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}/bridge/ws?${params.toString()}`;
  }

  async accept() {
    this.onState({ status: 'requesting_mic' });
    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });

    this.onState({ status: 'connecting' });
    this.ws = new WebSocket(this.bridgeUrl());
    this.ws.onopen = () => this.onState({ status: 'ws_open' });
    this.ws.onerror = e => this.onError({ source: 'ws', error: e });
    this.ws.onclose = () => this.onState({ status: 'ws_closed' });
    this.ws.onmessage = e => this._onWsMessage(e);

    await this._waitOpen();

    this.pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    this.localStream.getAudioTracks().forEach(t => this.pc.addTrack(t, this.localStream));
    this.pc.onicecandidate = e => {
      if (e.candidate) this._send({ type: 'ice', candidate: e.candidate });
    };
    this.pc.ontrack = e => {
      this.remoteStream = e.streams[0];
      this._playRemote();
    };
    this.pc.onconnectionstatechange = () => {
      this.onState({ status: 'pc_' + this.pc.connectionState });
    };

    const offer = await this.pc.createOffer({ offerToReceiveAudio: true });
    await this.pc.setLocalDescription(offer);
    this._send({ type: 'offer', sdp: offer.sdp });
  }

  async _onWsMessage(e) {
    let msg;
    try { msg = JSON.parse(e.data); } catch (_) { return; }

    if (msg.type === 'ready') {
      this.onState({ status: 'ready', meta: msg });
    } else if (msg.type === 'answer') {
      await this.pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp });
      this.onState({ status: 'answered' });
    } else if (msg.type === 'ice') {
      try { await this.pc.addIceCandidate(msg.candidate); }
      catch (err) { this.onError({ source: 'ice', error: err }); }
    } else if (msg.type === 'connected') {
      this.onState({ status: 'connected', acceptedAt: msg.accepted_at });
    } else if (msg.type === 'ended') {
      this.onState({ status: 'ended', reason: msg.reason });
      this.cleanup();
    } else if (msg.type === 'error') {
      this.onError({ source: 'bridge', error: msg.error, code: msg.code, message: msg.message, data: msg.data });
    } else if (msg.type === 'ringing') {
      this.onState({ status: 'ringing' });
    } else if (msg.type === 'meta_call_initiated') {
      this.onState({ status: 'meta_call_initiated', metaCallId: msg.meta_call_id });
    }
  }

  _send(obj) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  }

  _waitOpen() {
    return new Promise((resolve, reject) => {
      if (this.ws.readyState === WebSocket.OPEN) return resolve();
      this.ws.addEventListener('open', () => resolve(), { once: true });
      this.ws.addEventListener('error', err => reject(err), { once: true });
    });
  }

  _playRemote() {
    if (!this.audioEl) {
      this.audioEl = document.createElement('audio');
      this.audioEl.autoplay = true;
      this.audioEl.playsInline = true;
      this.audioEl.style.display = 'none';
      document.body.appendChild(this.audioEl);
    }
    this.audioEl.srcObject = this.remoteStream;
    this.audioEl.play().catch(err => this.onError({ source: 'audio', error: err }));
  }

  reject() {
    this._send({ type: 'reject' });
    this.cleanup();
  }

  async sendOneShotReject() {
    this.ws = new WebSocket(this.bridgeUrl());
    this.ws.onerror = e => this.onError({ source: 'ws', error: e });
    await this._waitOpen();
    this._send({ type: 'reject' });
    await new Promise(r => setTimeout(r, 300));
    this.cleanup();
  }

  hangup() {
    this._send({ type: 'hangup' });
    this.cleanup();
  }

  setMuted(muted) {
    this.localStream?.getAudioTracks().forEach(t => { t.enabled = !muted; });
  }

  cleanup() {
    try { this.localStream?.getTracks().forEach(t => t.stop()); } catch (_) { /* noop */ }
    try { this.pc?.close(); } catch (_) { /* noop */ }
    try { this.ws?.close(); } catch (_) { /* noop */ }
    try { this.audioEl?.remove(); } catch (_) { /* noop */ }
    this.localStream = null;
    this.pc = null;
    this.ws = null;
    this.audioEl = null;
  }
}
